/* eslint-disable @typescript-eslint/no-unnecessary-type-parameters */
import {
  LabOrderControllerService,
  UpdatePreferencesRequest,
  UserPreferencesControllerService,
  ZipCodeLookupControllerService
} from "@9amhealth/openapi";
import { Blac, Cubit } from "blac-next";
import type { Dict } from "mixpanel-browser";
import { globalEvents } from "src/constants/globalEvents";
import { LabProvider } from "src/constants/labProviders";
import type { Locale } from "src/constants/language";
import { stateCodeToNameMap } from "src/constants/stateCodeToNameMap";
import createTrackEvent from "src/lib/createTrackEvent";
import { dateGuessTz, dateLocal } from "src/lib/date";
import formatPhoneNumber from "src/lib/formatPhoneNumber";
import type { Journey } from "src/lib/getEducationalContent";
import { changeLanguage, getSupportedUserLocale } from "src/lib/i18next";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { apiMiddleware, historyState, tracker } from "src/state/state";
import AssumedIdentityCubit from "src/state/AssumedIdentityCubit/AssumedIdentityCubit";
import type { EducationalContent } from "src/ui/components/UserEducationalFeed/UserEducationalFeedBloc";
import { StorageController } from "../StorageBloc/StorageBloc";

export enum UserPreferenceKeys {
  onboardingDone = "onboarding.done",
  establishCareCompleted = "onboarding.establish-care-completed",
  healthInformationExchangeCompleted = "onboarding.health-information-exchange-completed",
  userDateOfBirth = "user.date-of-birth",
  userSex = "user.sex",
  userFirstName = "auth.user.firstname",
  userLastName = "auth.user.lastname",
  userNickname = "auth.user.nickname",
  userPronouns = "user.pronouns",
  registerFunnel = "user.register-funnel",
  partnerProgram = "user.partner.program",
  lastSignupFunnel = "user.last-signup-funnel",
  signupFunnelStatus = "user.signup-funnel-status",
  shipmentAddressAptSuite = "subscription.address.shipment.default.apt-suite",
  shipmentAddressCity = "subscription.address.shipment.default.city",
  shipmentAddressCountry = "subscription.address.shipment.default.country",
  shipmentAddressFirstName = "subscription.address.shipment.default.first-name",
  shipmentAddressLastName = "subscription.address.shipment.default.last-name",
  shipmentAddressState = "subscription.address.shipment.default.state",
  shipmentAddressStreet = "subscription.address.shipment.default.street",
  shipmentAddressZip = "subscription.address.shipment.default.zip",
  language = "user.language",
  timezone = "user.timezone",
  requestedPlanChanges = "user.requested-plan-changes",
  appUserPushNotificationAccessRequestAction = "app.user.push-notification-access-request-action",
  appUserBuckets = "app.user.buckets",
  pushNotificationsCareTeam = "notifications.care-team-message.push",
  smsNotificationsCareTeam = "notifications.care-team-message.sms",
  userHasDemoData = "user.has-demo-data",
  userEducationalContent = "user.careteamassigned.educationalcontent",
  physicianPrimaryPhone = "physicians.primary.phone-number",
  physicianPrimaryFax = "physicians.primary.fax-number",
  journeyEnrollment = "user.journey.enrollments",
  onboardingVersion = "subscription.onboarding-version",
  showManagementExpectation = "user.show-management-expectation-card",
  previousMessagesConversationId = "conversation.previous-messages.id",
  labsImportBannerDismissed = "labs.hie-banner-dismissed",
  chartVisibilityWeight = "user.charts.visibility.weight",
  chartVisibilityBodyComposition = "user.charts.visibility.body-composition",
  chartVisibilityWaistCircumference = "user.charts.visibility.waist-circumference",
  dailyInsightTutorial = "meals.daily-insight-tutorial"
}
export type UserPreferenceSex = "female" | "male";

export type DailyInsightTutorialOutcome = "completed" | "dismissed";

const VISIT_ONLY_ONBOARDING_PREFERENCE_VALUE =
  "PHASE_2_ONBOARDING_SYNC_VISIT_FIRST";

const UNBLOCKING_ONBOARDING_PREFERENCE_VALUE = "UNBLOCKED_ONBOARDING";

interface LabProviderCheckResult {
  error: string;
  providers: string[];
}

type UserPushRequestAction = "denied" | "granted" | "not-now";

export interface UserPreferences {
  loading: boolean | null;
  [UserPreferenceKeys.onboardingVersion]?: string;
  [UserPreferenceKeys.partnerProgram]?: string;
  [UserPreferenceKeys.onboardingDone]?: string;
  [UserPreferenceKeys.establishCareCompleted]?: boolean;
  [UserPreferenceKeys.healthInformationExchangeCompleted]?: boolean;
  [UserPreferenceKeys.userDateOfBirth]?: string;
  [UserPreferenceKeys.userSex]?: UserPreferenceSex;
  [UserPreferenceKeys.registerFunnel]?: string;
  [UserPreferenceKeys.lastSignupFunnel]?: string;
  [UserPreferenceKeys.signupFunnelStatus]?: string;
  [UserPreferenceKeys.shipmentAddressAptSuite]?: string;
  [UserPreferenceKeys.shipmentAddressCity]?: string;
  [UserPreferenceKeys.shipmentAddressCountry]?: string;
  [UserPreferenceKeys.shipmentAddressFirstName]?: string;
  [UserPreferenceKeys.shipmentAddressLastName]?: string;
  [UserPreferenceKeys.shipmentAddressState]?: string;
  [UserPreferenceKeys.shipmentAddressStreet]?: string;
  [UserPreferenceKeys.shipmentAddressZip]?: string;
  [UserPreferenceKeys.language]?: string;
  [UserPreferenceKeys.timezone]?: string;
  [UserPreferenceKeys.requestedPlanChanges]?: string;
  [UserPreferenceKeys.userFirstName]?: string;
  [UserPreferenceKeys.userLastName]?: string;
  [UserPreferenceKeys.userNickname]?: string;
  [UserPreferenceKeys.userPronouns]?: string;
  [UserPreferenceKeys.appUserBuckets]?: string[];
  [UserPreferenceKeys.appUserPushNotificationAccessRequestAction]?: UserPushRequestAction;
  [UserPreferenceKeys.pushNotificationsCareTeam]?: boolean;
  [UserPreferenceKeys.smsNotificationsCareTeam]?: boolean;
  [UserPreferenceKeys.userHasDemoData]?: boolean;
  [UserPreferenceKeys.userEducationalContent]?: EducationalContent;
  [UserPreferenceKeys.physicianPrimaryPhone]?: string;
  [UserPreferenceKeys.physicianPrimaryFax]?: string;
  [UserPreferenceKeys.journeyEnrollment]?: (Journey | undefined)[];
  [UserPreferenceKeys.showManagementExpectation]?: boolean;
  [UserPreferenceKeys.previousMessagesConversationId]?: string | null;
  [UserPreferenceKeys.labsImportBannerDismissed]?: boolean;
  [UserPreferenceKeys.chartVisibilityWeight]?: boolean;
  [UserPreferenceKeys.chartVisibilityBodyComposition]?: boolean;
  [UserPreferenceKeys.chartVisibilityWaistCircumference]?: boolean;
  [UserPreferenceKeys.dailyInsightTutorial]?: DailyInsightTutorialOutcome;
}

export const ChartVisibilityPreferenceKeys = [
  UserPreferenceKeys.chartVisibilityWeight,
  UserPreferenceKeys.chartVisibilityBodyComposition,
  UserPreferenceKeys.chartVisibilityWaistCircumference
] as const;

export type ChartVisibilityPreference =
  (typeof ChartVisibilityPreferenceKeys)[number];

export const chartVisibilityDependencies = (
  state: UserPreferences
): (boolean | undefined)[] =>
  ChartVisibilityPreferenceKeys.map((key) => state[key]);

const pickKeys = <T extends object>(source: T, keys: (keyof T)[]): Partial<T> =>
  Object.fromEntries(
    keys.filter((key) => key in source).map((key) => [key, source[key]])
  ) as Partial<T>;

const reflectPreferencesInProfile: UserPreferenceKeys[] = [
  UserPreferenceKeys.appUserPushNotificationAccessRequestAction,
  UserPreferenceKeys.lastSignupFunnel,
  UserPreferenceKeys.registerFunnel,
  UserPreferenceKeys.signupFunnelStatus
];

export default class UserPreferencesCubit extends Cubit<UserPreferences> {
  static keepAlive = true;
  setupDone = false;

  /**
   * The id of the newest optimistic write per key, kept after the request
   * settles rather than only while it is in flight. Two stale-response problems
   * are settled from this: a load issued before a write must not apply the
   * server's older value for that key even if the load resolves last, and a
   * failed write must not roll back a value a later write already replaced.
   */
  private readonly writeVersions = new Map<keyof UserPreferences, number>();
  private writeCounter = 0;

  constructor() {
    super({
      loading: false
    });
    window.addEventListener(globalEvents.USER_CLEAR, () => {
      this.writeVersions.clear();
      this.emit({
        loading: false
      });
    });

    // Listen for actual user changes (not just token refresh)
    // USER_CHANGED only fires when the user ID actually changes
    window.addEventListener(globalEvents.USER_CHANGED, () => {
      // Reset state and reload preferences for the new user
      this.writeVersions.clear();
      this.emit({ loading: false });
      void this.loadUserPreferences();
    });

    const cached =
      StorageController.activeUserId &&
      StorageController.getItem(this.cacheKey);
    if (cached) {
      this.emit(JSON.parse(cached) as UserPreferences);
    }
  }

  cacheKey = "preferences";
  emitAndCache = (state: UserPreferences): void => {
    this.emit(state);
    StorageController.setItem(
      this.cacheKey,
      JSON.stringify({ ...state, loading: false })
    );
  };

  reset = (): void => {
    this.writeVersions.clear();
    this.emit({
      loading: false
    });
    this.setupDone = false;
  };

  /**
   * Applies a server payload without disturbing keys written after the request
   * that produced it was issued: such a response left before the tap and cannot
   * describe the value the member just chose. Keys written but absent from state
   * are removed rather than carried over as `undefined`, matching
   * `revertPreferences`.
   *
   * `issuedAt` is the write counter sampled before the request went out.
   */
  private readonly preserveWritesSince = (
    next: UserPreferences,
    issuedAt: number
  ): UserPreferences => {
    const merged: UserPreferences = { ...next };

    for (const [key, version] of this.writeVersions) {
      if (version <= issuedAt) {
        continue;
      }

      if (key in this.state) {
        Reflect.set(merged, key, Reflect.get(this.state, key));
      } else {
        Reflect.deleteProperty(merged, key);
      }
    }

    return merged;
  };

  /**
   * Reports which of `keys` the given write is still the newest for, dropping
   * their versions so a later load may apply server values again. A key claimed
   * by a newer write is not returned, so a failed older request cannot roll it
   * back.
   */
  private readonly claimStaleWrites = (
    keys: (keyof UserPreferences)[],
    writeId: number
  ): (keyof UserPreferences)[] => {
    const owned = keys.filter((key) => this.writeVersions.get(key) === writeId);

    for (const key of owned) {
      this.writeVersions.delete(key);
    }

    return owned;
  };

  readonly setupUserProperties = async (): Promise<void> => {
    // load the user preferences
    await this.loadUserPreferences();

    // all preferences that need to be updated
    const updatedPreferences: Partial<UserPreferences> = {};

    // get the user preferred language from browser settings
    const supportedLocale =
      historyState.initialLocale ?? getSupportedUserLocale();

    if (typeof this.state[UserPreferenceKeys.language] === "undefined") {
      // Set the language preference
      updatedPreferences[UserPreferenceKeys.language] = supportedLocale;
    }

    // get user timezone
    const timezone = dateGuessTz();
    if (timezone !== this.state[UserPreferenceKeys.timezone]) {
      // Set the timezone preference
      updatedPreferences[UserPreferenceKeys.timezone] = timezone;
    }

    // An assumed identity must not stamp the operator's locale/timezone onto the member.
    const isAssumedIdentity = Blac.getBloc(AssumedIdentityCubit).isActive;
    if (Object.keys(updatedPreferences).length > 0 && !isAssumedIdentity) {
      await this.updateUserPreferences(updatedPreferences);
    }

    this.setupDone = true;
  };

  fetchPreferences = async (): Promise<UserPreferences | undefined> =>
    apiMiddleware.cached(
      async () => (await UserPreferencesControllerService.getPreferences()).data
    ) as unknown as UserPreferences;

  public readonly loadUserPreferences = async (): Promise<
    UserPreferences | undefined
  > => {
    const issuedAt = this.writeCounter;
    this.emitAndCache({ ...this.state, loading: true });

    try {
      // If we have a promise, we are already loading
      const data = await this.fetchPreferences();
      if (data?.[UserPreferenceKeys.language]) {
        void changeLanguage(data[UserPreferenceKeys.language]);
      }

      this.emitAndCache(
        this.preserveWritesSince(
          {
            ...data,
            loading: false
          },
          issuedAt
        )
      );

      return data;
    } catch (e: unknown) {
      reportErrorSentry(e);
      this.emit({
        loading: false
      });
    }
  };

  parsePreferenceValue = <
    K extends keyof UserPreferences,
    V = UserPreferences[K]
  >(
    key: K,
    value: V
  ): V => {
    switch (key) {
      case UserPreferenceKeys.physicianPrimaryPhone:
      case UserPreferenceKeys.physicianPrimaryFax:
        return formatPhoneNumber(String(value)) as V;

      default:
        return value;
    }
  };

  prepareDataForParser = <
    K extends keyof UserPreferences,
    V = UserPreferences[K]
  >(
    preferences: Partial<UserPreferences>
  ): UpdatePreferencesRequest => {
    const newObject: Partial<Record<K, V>> = {};

    for (const key in preferences) {
      if (key !== "loading") {
        let value = preferences[key as K] as V;
        value = this.parsePreferenceValue(key as K, value);
        newObject[key as K] = value;
      }
    }

    return newObject as UpdatePreferencesRequest;
  };

  /**
   * Only a write that carries a language should switch it. Re-applying the
   * language already in state is a no-op at best, and falls back to English
   * when the preference has not been read yet.
   */
  private readonly applyLanguagePreference = (
    newPreferences: Partial<UserPreferences>
  ): void => {
    const language = newPreferences[UserPreferenceKeys.language] as
      | Locale
      | undefined;

    if (language) {
      void changeLanguage(language);
    }
  };

  private readonly trackPreferenceUpdate = (
    newPreferences: Partial<UserPreferences>
  ): void => {
    const propsForTracking = Object.keys(newPreferences).reduce<Dict>(
      (acc, key) => {
        const shouldReflectPreferencesInProfile =
          reflectPreferencesInProfile.find((k) => key.indexOf(k) > -1);
        const value = (newPreferences as Dict)[key] as string | undefined;
        if (value && shouldReflectPreferencesInProfile) {
          acc[key] = value;
        }
        return acc;
      },
      {}
    );

    tracker.setCustomUserAttributes(propsForTracking);
  };

  /**
   * Puts the given keys back to the values they held before an optimistic
   * write. Keys that were absent are removed again rather than left as
   * `undefined`, so an opt-out preference does not silently read as its default.
   */
  private readonly revertPreferences = (
    written: Partial<UserPreferences>,
    previous: Partial<UserPreferences>
  ): void => {
    const restored: UserPreferences = { ...this.state, ...previous };

    for (const key of Object.keys(written)) {
      if (!(key in previous)) {
        Reflect.deleteProperty(restored, key);
      }
    }

    this.emitAndCache(restored);
  };

  /**
   * Writes preferences without touching the shared `loading` flag: the new
   * values are in state before the request leaves, and only the written keys
   * roll back if it fails.
   *
   * Toggles use this instead of `updateUserPreferences`, which marks the whole
   * preferences state as loading for the length of the round trip — blanking
   * every consumer gated on that flag — and clears the app-wide API cache.
   */
  public readonly updateUserPreferencesOptimistic = async (
    newPreferences: Partial<UserPreferences>
  ): Promise<void> => {
    this.applyLanguagePreference(newPreferences);

    const keys = Object.keys(newPreferences) as (keyof UserPreferences)[];
    const previous = pickKeys(this.state, keys);
    const writeId = ++this.writeCounter;

    for (const key of keys) {
      this.writeVersions.set(key, writeId);
    }

    this.emitAndCache({
      ...this.state,
      ...newPreferences
    });

    try {
      const sendData = this.prepareDataForParser(newPreferences);
      await UserPreferencesControllerService.updatePreferences(sendData);
    } catch (e: unknown) {
      reportErrorSentry(e);
      const owned = this.claimStaleWrites(keys, writeId);

      if (owned.length > 0) {
        this.revertPreferences(
          pickKeys(newPreferences, owned),
          pickKeys(previous, owned)
        );
      }
      return;
    }

    this.trackPreferenceUpdate(newPreferences);
  };

  public readonly updateUserPreferences = async (
    newPreferences: Partial<UserPreferences>
  ): Promise<void> => {
    this.applyLanguagePreference(newPreferences);

    const issuedAt = this.writeCounter;

    this.emitAndCache({
      ...this.state,
      loading: true,
      ...newPreferences
    });

    try {
      const sendData = this.prepareDataForParser(newPreferences);
      const { data } =
        await UserPreferencesControllerService.updatePreferences(sendData);

      apiMiddleware.clearAll();

      this.emitAndCache(
        this.preserveWritesSince(
          {
            ...data,
            loading: false
          },
          issuedAt
        )
      );

      this.trackPreferenceUpdate(newPreferences);
    } catch (e: unknown) {
      reportErrorSentry(e);
      this.emitAndCache({
        ...this.state,
        loading: false
      });
    }
  };

  public readonly checkZipCode = async (
    zipCode: string
  ): Promise<{ state: string } | undefined> => {
    let result = undefined;

    try {
      const { data } =
        await ZipCodeLookupControllerService.getStateByZipCode(zipCode);
      result = { state: data.state };
    } catch (e: unknown) {
      const networkError = e as NetworkError | undefined;

      if (networkError?.status !== 404) {
        reportErrorSentry(e);
      }
    }

    return result;
  };

  public readonly checkAvailableLabOrderProviders = async (
    zipCode: string
  ): Promise<LabProviderCheckResult> => {
    try {
      // check if zip is valid
      const zipCheckResponse = await this.checkZipCode(zipCode);
      const stateName = stateCodeToNameMap[zipCheckResponse?.state ?? ""];
      if (!stateName) {
        return {
          error: "invalid_zip_code",
          providers: []
        };
      }

      // set the users zip code in preferences
      await this.updateUserPreferences({
        [UserPreferenceKeys.shipmentAddressZip]: zipCode
      });

      // get the supported providers for the user
      const availableProvidersRes =
        await LabOrderControllerService.availableLabOrderProviders();

      // track the zip code check
      tracker.track(createTrackEvent("Check Available Lab Providers"), {
        data: {
          "ZIP Code": zipCode,
          State: stateName,
          Providers: availableProvidersRes.data.labOrderProviders
        }
      });

      return {
        error: "",
        providers: availableProvidersRes.data.labOrderProviders
      };
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error: unknown) {
      return {
        error: "error_generic",
        providers: []
      };
    }
  };

  public readonly isHomePhlebotomySupported = (
    availableProviders: string[]
  ): boolean =>
    UserPreferencesCubit.isHomePhlebotomySupported(availableProviders);

  static isHomePhlebotomySupported = (
    availableProviders: string[]
  ): boolean => {
    const atHomeLabSupported =
      availableProviders.includes(LabProvider.BIO_REFERENCE_LAB) ||
      availableProviders.includes(LabProvider.GETLABS);

    return atHomeLabSupported;
  };

  public readonly checkUserHasShippingAddress = async (): Promise<boolean> => {
    try {
      await this.loadUserPreferences();
      const street = this.state[UserPreferenceKeys.shipmentAddressStreet];

      return Boolean(street);
    } catch (error) {
      reportErrorSentry(error);
    }

    return false;
  };

  /**
   * GETTER HELPERS FOR USER PREFERENCES
   */

  get firstName(): string | undefined {
    return this.state[UserPreferenceKeys.userFirstName];
  }

  get lastName(): string | undefined {
    return this.state[UserPreferenceKeys.userLastName];
  }

  get nickname(): string | undefined {
    return this.state[UserPreferenceKeys.userNickname];
  }

  get personalName(): string {
    const { firstName, nickname } = this;
    return nickname ?? firstName ?? "";
  }

  get pronouns(): string | undefined {
    return this.state[UserPreferenceKeys.userPronouns];
  }

  get language() {
    return this.state[UserPreferenceKeys.language];
  }

  get displayName(): string | undefined {
    return `${
      this.state[UserPreferenceKeys.userFirstName] ??
      this.state[UserPreferenceKeys.shipmentAddressFirstName]
    } ${
      this.state[UserPreferenceKeys.userLastName] ??
      this.state[UserPreferenceKeys.shipmentAddressLastName]
    }`;
  }

  get showManagementExpectation(): boolean {
    return Boolean(this.state[UserPreferenceKeys.showManagementExpectation]);
  }

  get isLabsImportBannerDismissed(): boolean {
    return Boolean(this.state[UserPreferenceKeys.labsImportBannerDismissed]);
  }

  get pushNotificationsCareTeamEnabled(): boolean {
    const pref =
      this.state[UserPreferenceKeys.pushNotificationsCareTeam] ?? true;
    return Boolean(pref);
  }

  get smsNotificationsCareTeamEnabled(): boolean {
    const pref =
      this.state[UserPreferenceKeys.smsNotificationsCareTeam] ?? true;
    return Boolean(pref);
  }

  /**
   * Chart visibility (FEWEB-2587) is opt-out: a member who has never touched the
   * setting still sees their data, so an absent key has to read as visible.
   */
  isChartVisible = (key: UserPreferenceKeys): boolean =>
    Boolean(this.state[key as keyof UserPreferences] ?? true);

  get weightChartVisible(): boolean {
    return this.isChartVisible(UserPreferenceKeys.chartVisibilityWeight);
  }

  get bodyCompositionChartVisible(): boolean {
    return this.isChartVisible(
      UserPreferenceKeys.chartVisibilityBodyComposition
    );
  }

  get waistCircumferenceChartVisible(): boolean {
    return this.isChartVisible(
      UserPreferenceKeys.chartVisibilityWaistCircumference
    );
  }

  get lastSignupFunnel(): string | undefined {
    return this.state[UserPreferenceKeys.lastSignupFunnel];
  }

  public get journeys() {
    return this.state[UserPreferenceKeys.journeyEnrollment] ?? [];
  }

  get dateOfBirth(): string | undefined {
    return this.state[UserPreferenceKeys.userDateOfBirth];
  }

  get userAge(): number | undefined {
    const dob = this.state[UserPreferenceKeys.userDateOfBirth];
    if (!dob) {
      return undefined;
    }
    const asDate = dateLocal(dob);
    const now = dateLocal();
    const age = now.diff(asDate, "years");
    return age;
  }

  get isDemoUser(): boolean {
    return Boolean(this.state[UserPreferenceKeys.userHasDemoData]);
  }

  get isEstablishCareCompleted(): boolean {
    return Boolean(this.state[UserPreferenceKeys.establishCareCompleted]);
  }

  get isHealthInformationExchangeCompleted(): boolean {
    return Boolean(
      this.state[UserPreferenceKeys.healthInformationExchangeCompleted]
    );
  }

  get userHasVisitOnlyOnboarding(): boolean {
    return (
      this.state[UserPreferenceKeys.onboardingVersion] ===
      VISIT_ONLY_ONBOARDING_PREFERENCE_VALUE
    );
  }

  get userHasUnblockingOnboarding(): boolean {
    return (
      this.state[UserPreferenceKeys.onboardingVersion] ===
      UNBLOCKING_ONBOARDING_PREFERENCE_VALUE
    );
  }
}
